/**
 * Složené widget komponenty pro layoutWidgets.tsx. Žijí v samostatném souboru, protože
 * layoutWidgets.tsx exportuje `renderWidget` (funkci, ne komponentu) — kdyby komponenty
 * bydlely tam, Fast Refresh by pro ně nefungoval (react-refresh/only-export-components).
 */

import { Columns2, LayoutGrid } from "lucide-react";
import React from "react";
import { t } from "@client/lib/dictionary";
import { useUIStore } from "@client/store/ui";
import Foscams from "@client/components/Foscams";
import { SubscribedChart } from "@client/components/valueDisplays/SubscribedChart";
import { useDeviceTopicLive } from "@client/hooks/useDeviceTopicLive";
import DailyHistory from "@client/components/valueDisplays/Stats";
import HourlyStats from "@client/components/valueDisplays/HourlyStats";
import HidingWrapper from "@client/components/wrappers/HidingWrapper";
import PartialWrapper, { BTN_CLASS } from "@client/components/wrappers/PartialWrapper";
import { useUserStore } from "@client/store/user";

export const Placeholder: React.FC<{ label: string }> = ({ label }) => (
  <div className="flex h-full min-h-16 items-center justify-center rounded border border-dashed border-border bg-muted/30 p-2 text-center text-xs text-muted-foreground">
    {label}
  </div>
);

/** Kamery — schované při načtení (PartialWrapper showInitially=false), bez controls, jako originál. */
export const CamerasWidget: React.FC = () => {
  const foscams = useUserStore((s) => s.foscams);
  const layout = useUIStore((s) => s.foscamLayout);
  const toggleLayout = useUIStore((s) => s.toggleFoscamLayout);
  const doQuad = layout === "auto";

  // Přepínač rozložení patří k hlavičce wrapperu, vedle fullscreenu — proto slotem
  // `actions`, ne dovnitř mřížky. Volba se ukládá, takže přežije refresh.
  const prepinac = (
    <button
      type="button"
      onClick={toggleLayout}
      className={BTN_CLASS}
      aria-label={doQuad ? t.foscam.layoutToQuad : t.foscam.layoutToAuto}
      title={doQuad ? t.foscam.layoutToQuad : t.foscam.layoutToAuto}
    >
      {doQuad ? <LayoutGrid className="h-5 w-5" /> : <Columns2 className="h-5 w-5" />}
    </button>
  );

  return (
    <PartialWrapper topicIds={foscams.map((f) => f.name)} showInitially={false} actions={prepinac}>
      <Foscams foscams={foscams} topicIds={foscams.map((f) => f.name)} />
    </PartialWrapper>
  );
};

/** Skrývací historie (denní + hodinová) — jako HidingWrapper sekce v originále.
 *  onlyVisibility=true → children zůstávají mountnuté (CSS hidden); false (default) → unmount při zavření. */
export const HistoryWidget: React.FC<{ topicId: string; boolean?: boolean; onlyVisibility?: boolean }> = ({
  topicId,
  boolean,
  onlyVisibility,
}) => (
  <HidingWrapper topicId={topicId} showInitially={false} onlyVisibility={onlyVisibility}>
    <DailyHistory topicId={topicId} boolean={boolean} />
    <HourlyStats topicId={topicId} boolean={boolean} />
  </HidingWrapper>
);

/** Wind sekce: vnořené HidingWrappery s onlyVisibility (graf + skrývací historie pod jedním headerem) — věrně dle originálu (App.tsx). */
export const WindSectionWidget: React.FC<{ topicId: string; boolean?: boolean }> = ({ topicId, boolean }) => (
  <HidingWrapper topicId={topicId} showInitially={false} onlyVisibility={true}>
    <SubscribedChart topicIds={[topicId]} boolean={!!boolean} />
    <HidingWrapper topicId={topicId} showInitially={false} onlyVisibility={true}>
      <DailyHistory topicId={topicId} boolean={boolean} />
      <HourlyStats topicId={topicId} boolean={boolean} />
    </HidingWrapper>
  </HidingWrapper>
);

/** Graf: řada zůstává 15s MQTT historie, ale hlavička ukazuje ŽIVOU ~1 Hz hodnotu (device telemetrie),
 *  jako původní *Charts (liveValue). Jen pro single-topic; vícetopicové grafy bez live hlavičky. */
export const ChartWidget: React.FC<{ topics: string[]; boolean: boolean }> = ({ topics, boolean }) => {
  const live = useDeviceTopicLive(topics.length === 1 ? topics[0] : "");
  const liveValue = topics.length === 1 && live && !live.stale ? live.value : undefined;
  return <SubscribedChart topicIds={topics} boolean={boolean} liveValue={liveValue} />;
};
